/**
 * Как пункт плана выглядит подходами — общее для тренировки и дня программы.
 *
 * День программы с 14.09.2026 рисуется теми же карточками, что тренировка, и
 * расходиться в том, сколько строк у дропсета или что сцеплено с чем, они не
 * должны: иначе в конструкторе одно, а в зале другое.
 */

interface Chainable {
  is_backoff: boolean;
  superset_group: number | null;
}

/** Основное упражнение и его добивка живут одной сцепкой — как в программе. */
export function groupChained<T extends Chainable>(items: T[]): T[][] {
  const groups: T[][] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    const chained =
      last &&
      (item.is_backoff ||
        (item.superset_group !== null && item.superset_group === last[0].superset_group));
    if (chained) last.push(item);
    else groups.push([item]);
  }
  return groups;
}

interface Plannable {
  target_sets: number | null;
  target_reps_min: number | null;
  target_reps_max: number | null;
  rep_sequence: number[] | null;
}

export interface PlannedSet {
  /** Номер рабочего подхода; null — сброс дропсета. */
  number: number | null;
  reps: string;
}

/**
 * Строки плана. Правило то же, что у тренировки (setRows.plannedKinds): у
 * дропсета строк столько, сколько чисел в последовательности, и все после
 * первой — сбросы; иначе — `target_sets` строк с диапазоном повторов.
 */
export function plannedSets(item: Plannable): PlannedSet[] {
  if (item.rep_sequence?.length) {
    return item.rep_sequence.map((reps, i) => ({ number: i === 0 ? 1 : null, reps: String(reps) }));
  }
  const { target_reps_min: min, target_reps_max: max } = item;
  const reps =
    min === null ? "—" : max !== null && max !== min ? `${min}–${max}` : String(min);
  return Array.from({ length: item.target_sets ?? 1 }, (_, i) => ({ number: i + 1, reps }));
}

/** 90 → «1:30», 45 → «0:45». */
export const restText = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
